import { BFF_ROUTES } from '@rheora/enrollment-contract';

import { bffGetBlob, type BffBlob } from './bff-client';

/**
 * Typed fetcher for the E-SF18 unsigned contract preview. Thin: request + hand
 * back the bytes. Pages call the `agreement.queries` hook, never this.
 *
 * ── WHY THERE IS NO SCHEMA HERE ────────────────────────────────────────────
 * Every other fetcher in this folder validates a JSON body against the shared
 * contract schema. This one CANNOT, and that is the point: the BFF streams PDF
 * bytes precisely so there is no JSON body carrying the premise. `bffGetBlob`
 * still enforces what can be enforced on a byte stream — a non-2xx becomes a
 * typed `BffError`, and a zero-byte 200 is refused rather than rendered as a
 * blank contract.
 */

/** Fallback used only when the BFF sends no usable `content-disposition`. */
const PREVIEW_FALLBACK_FILENAME = 'rheora-agreement-preview.pdf';

export interface AgreementPreviewArgs {
  /** The opaque, session-scoped premise handle (E-SF03). */
  readonly candidateId: string;
  /**
   * The tariff being signed. REQUIRED: the BFF resolves the DRAFT quote by the
   * enrollment fingerprint `(candidate, plan, ESIID)`, so omitting the plan would
   * either fail or — worse, if the server were ever loosened — return the contract
   * for a plan the shopper had already navigated away from.
   */
  readonly planId: string;
  readonly signal?: AbortSignal;
}

/** Fetch the UNSIGNED contract PDF for this enrollment. */
export async function getAgreementPreview(args: AgreementPreviewArgs): Promise<BffBlob> {
  const path =
    `${BFF_ROUTES.signAgreementPreview}?candidate=${encodeURIComponent(args.candidateId)}` +
    `&plan=${encodeURIComponent(args.planId)}`;
  return bffGetBlob(path, {
    accept: 'application/pdf',
    fallbackFilename: PREVIEW_FALLBACK_FILENAME,
    ...(args.signal === undefined ? {} : { signal: args.signal }),
  });
}
